// src/features/marketResearch/components/sections/SupplyAnalysis.tsx
import React from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Paper,
  Grid,
  Box,
  useTheme
} from '@mui/material';
import { Domain as DomainIcon } from '@mui/icons-material';
import {
  SectionContainer,
  SectionTitle,
  SectionBody,
  MetricHighlight,
  SubSectionTitle
} from '../Section';

interface SupplyData {
  existingSupply?: string;
  pipelineProjects?: any[];
  supplyGrowth?: string;
}

interface Props {
  data?: SupplyData;
}

// Helper function to safely convert any value to string
const safeToString = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    // Check if it's a project object with name or description
    if ('name' in value) return String(value.name);
    if ('description' in value) return String(value.description);
    try {
      return JSON.stringify(value);
    } catch {
      return '[Object]';
    }
  }
  return String(value);
};

export const SupplyAnalysis: React.FC<Props> = ({ data }) => {
  const theme = useTheme();
  const existing = data?.existingSupply ?? '';
  const projects = data?.pipelineProjects ?? [];
  const growth = data?.supplyGrowth ?? '';

  // Simple helpers to extract numeric insights for metric highlights
  const extractNumber = (text: string) => {
    const match = text.match(/\d+[\d,]*/);
    return match ? match[0] : 'N/A';
  };

  const existingMetric = existing ? extractNumber(existing) : 'N/A';
  const growthMetric = growth ? (growth.match(/\d+%/) ? growth.match(/\d+%/)![0] : extractNumber(growth)) : 'N/A';

  return (
    <SectionContainer id="supply-analysis">
      <SectionTitle>Supply Analysis</SectionTitle>
      <SectionBody>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            mb: 4,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper
          }}
        >
          {/* Metric Highlights */}
          <Box
            sx={{
              display: 'flex',
              gap: 2.5,
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              mb: 4,
              pb: 3,
              borderBottom: `1px solid ${theme.palette.divider}`
            }}
          >
            <MetricHighlight label="Existing Supply" value={existingMetric} color={theme.palette.info.main} />
            <MetricHighlight label="Pipeline Projects" value={projects.length} color={theme.palette.primary.main} />
            <MetricHighlight label="Growth Outlook" value={growthMetric} color={theme.palette.success.main} />
          </Box>

          <Grid container spacing={3}>
            {/* Existing Supply */}
            <Grid item xs={12} md={6}>
              <SubSectionTitle>Existing Supply Overview</SubSectionTitle>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                {existing || 'No detailed existing supply data provided.'}
              </Typography>
            </Grid>

            {/* Supply Growth */}
            <Grid item xs={12} md={6}>
              <SubSectionTitle>Supply Growth Outlook</SubSectionTitle>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                {growth || 'Supply growth data not available.'}
              </Typography>
            </Grid>

            {/* Pipeline Projects */}
            <Grid item xs={12}>
              <SubSectionTitle>Pipeline Projects</SubSectionTitle>
              {projects.length > 0 ? (
                <List>
                  {projects.map((proj, i) => (
                    <ListItem key={i} sx={{ py: 0.75 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <DomainIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={safeToString(proj)}
                        primaryTypographyProps={{ variant: 'body2', sx: { lineHeight: 1.6 } }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No projects in the pipeline.
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>
      </SectionBody>
    </SectionContainer>
  );
};
